const COLUMN_ALIGNMENT = 8
const FNV_OFFSET_BASIS = 0x811c9dc5
const FNV_PRIME = 0x01000193
const SAMPLED_WORDS = 64
const BYTES_PER_WORD = 4

export interface ColumnPlacement {
  offset: number
  length: number
}

interface StoredColumn {
  offset: number
  bytes: Uint8Array
}

export class ColumnWriter {
  private readonly columns: StoredColumn[] = []
  private readonly columnsByContent = new Map<string, StoredColumn[]>()
  private size = 0

  add(data: Float64Array | Uint8Array): ColumnPlacement {
    const bytes = new Uint8Array(
      new Uint8Array(data.buffer, data.byteOffset, data.byteLength)
    )
    const key = `${bytes.byteLength}:${sampledHash(bytes)}`
    const sameKey = this.columnsByContent.get(key) ?? []
    const stored = sameKey.find((column) => sameBytes(column.bytes, bytes))
    if (stored) return { offset: stored.offset, length: data.length }
    const column = { offset: this.size, bytes }
    this.columns.push(column)
    this.columnsByContent.set(key, [...sameKey, column])
    this.size = alignUp(column.offset + bytes.byteLength)
    return { offset: column.offset, length: data.length }
  }

  bytes(): Uint8Array {
    const all = new Uint8Array(this.size)
    for (const { offset, bytes } of this.columns) all.set(bytes, offset)
    return all
  }
}

export function alignUp(size: number): number {
  return Math.ceil(size / COLUMN_ALIGNMENT) * COLUMN_ALIGNMENT
}

function sampledHash(bytes: Uint8Array): number {
  const words = wordsOf(bytes)
  const step = Math.max(1, Math.floor(words.length / SAMPLED_WORDS))
  let hash = FNV_OFFSET_BASIS
  for (let i = 0; i < words.length; i += step) {
    hash = Math.imul(hash ^ words[i], FNV_PRIME)
  }
  for (let i = words.byteLength; i < bytes.length; i++) {
    hash = Math.imul(hash ^ bytes[i], FNV_PRIME)
  }
  return hash >>> 0
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.byteLength !== b.byteLength) return false
  const wordsA = wordsOf(a)
  const wordsB = wordsOf(b)
  for (let i = 0; i < wordsA.length; i++) {
    if (wordsA[i] !== wordsB[i]) return false
  }
  for (let i = wordsA.byteLength; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

function wordsOf(bytes: Uint8Array): Uint32Array {
  return new Uint32Array(
    bytes.buffer,
    bytes.byteOffset,
    Math.floor(bytes.byteLength / BYTES_PER_WORD)
  )
}
